import { generateCelebrity } from './game/celebrityGenerator.js';
import { answerQuestion, verifyGuess } from './game/guessVerifier.js';
import { generateCase } from './game/caseGenerator.js';
import { answerCaseQuestion, verifyReconstruction } from './game/caseVerifier.js';
import { LLMClient } from './llm/llmClient.js';

export class App {
  constructor(config, onConfigChange, llmClient, gameState, viewManager) {
    this.config = config;
    this.onConfigChange = onConfigChange;
    this.llmClient = llmClient;
    this.gameState = gameState;
    this.viewManager = viewManager;
    this.usedNames = this.loadUsedNames();
    this.usedCases = this.loadUsedCases();
    this.isLoading = false;
  }

  init() {
    this.viewManager.init();
    this.viewManager.setCallbacks({
      saveSettings: () => this.saveSettings(),
      testConnection: () => this.testConnection(),
      newGame: () => this.startNewGame(),
      questionSubmit: () => this.submitQuestion(),
      guessSubmit: () => this.submitGuess(),
      giveUp: () => this.giveUp(),
      stealthModeChange: (enabled) => this.setStealthMode(enabled),
    });

    this.viewManager.setSettings(this.config);
    this.viewManager.updateSettingsSummary(this.config);
    this.gameState.setConfig(this.config);
    this.render();
  }

  saveSettings() {
    const newConfig = this.viewManager.getSettings();
    const modeChanged = newConfig.gameMode !== this.config.gameMode;
    this.config = { ...this.config, ...newConfig };
    this.gameState.setConfig(this.config);
    this.onConfigChange(this.config);
    this.viewManager.updateSettingsSummary(this.config);
    this.viewManager.showSettingsMessage('设置已保存', 'success');
    this.viewManager.activeView.closeSettingsModal?.();

    if (modeChanged) {
      this.gameState.newGame();
      this.render();
    }
  }

  async testConnection() {
    const settings = this.viewManager.getSettings();
    if (!settings.apiKey) {
      this.viewManager.showSettingsMessage('请先填写 API Key', 'error');
      return;
    }

    this.viewManager.showSettingsMessage('正在测试连接...', 'info');
    try {
      const client = new LLMClient(settings);
      await client.send([
        { role: 'system', content: 'You are a helpful assistant.' },
        { role: 'user', content: 'Say "OK" only.' },
      ]);
      this.viewManager.showSettingsMessage('连接成功', 'success');
    } catch (err) {
      this.viewManager.showSettingsMessage(`连接失败: ${err.message}`, 'error');
    }
  }

  async startNewGame() {
    if (!this.config.apiKey) {
      this.viewManager.showGameMessage('请先在设置中保存 API Key', 'error');
      return;
    }

    this.setLoading(true);
    this.viewManager.clearGameMessage();

    try {
      if (this.config.gameMode === 'detective') {
        const caseObj = await generateCase(this.llmClient, {
          difficulty: this.config.difficulty,
          excludeList: this.usedCases,
        });
        this.gameState.newGame();
        this.gameState.setTarget(caseObj);
        this.usedCases.push(caseObj.id);
        this.saveUsedCases();
      } else {
        const celebrity = await generateCelebrity(this.llmClient, {
          difficulty: this.config.difficulty,
          field: this.config.field,
          excludeList: this.usedNames,
        });
        this.gameState.newGame();
        this.gameState.setTarget(celebrity);
        this.usedNames.push(celebrity.name);
        this.saveUsedNames();
      }
      this.render();
    } catch (err) {
      const label = this.config.gameMode === 'detective' ? '海龟汤' : '名人';
      this.viewManager.showGameMessage(`生成${label}失败: ${err.message}`, 'error');
    } finally {
      this.setLoading(false);
    }
  }

  async submitQuestion() {
    const state = this.gameState.get();
    if (state.stage !== 'playing') return;

    const question = this.viewManager.getQuestionInput() || this.viewManager.getChatInput();
    if (!question) return;

    this.setLoading(true);
    this.viewManager.clearQuestionInput();
    this.viewManager.clearChatInput();

    try {
      let result;
      if (this.config.gameMode === 'detective') {
        result = await answerCaseQuestion(this.llmClient, {
          case: state.target,
          history: state.history,
          question,
        });
      } else {
        result = await answerQuestion(this.llmClient, {
          celebrity: state.target,
          history: state.history,
          question,
        });
      }

      this.gameState.addQuestion(result);
      this.render();
    } catch (err) {
      this.viewManager.showGameMessage(`回答失败: ${err.message}`, 'error');
    } finally {
      this.setLoading(false);
    }
  }

  async submitGuess() {
    const state = this.gameState.get();
    if (state.stage !== 'playing') return;

    const guess = this.viewManager.getGuessInput() || this.viewManager.getChatInput();
    if (!guess) return;

    this.setLoading(true);
    this.viewManager.clearGuessInput();
    this.viewManager.clearChatInput();

    try {
      let result;
      if (this.config.gameMode === 'detective') {
        result = await verifyReconstruction(this.llmClient, {
          case: state.target,
          history: state.history,
          reconstruction: guess,
        });
      } else {
        result = await verifyGuess(this.llmClient, {
          celebrity: state.target,
          history: state.history,
          guess,
        });
      }

      this.gameState.addGuess(result);

      if (this.config.gameMode === 'detective' && !result.correct) {
        this.gameState.giveUp();
      }

      this.render();
    } catch (err) {
      this.viewManager.showGameMessage(`判断失败: ${err.message}`, 'error');
    } finally {
      this.setLoading(false);
    }
  }

  async giveUp() {
    const state = this.gameState.get();
    if (!state.target) return;

    this.setLoading(true);

    try {
      this.gameState.addGuess({
        type: 'guess',
        input: '（放弃）',
        correct: false,
        matchConfidence: 'wrong',
        feedback: '已放弃，查看下方汤底',
        reason: '',
      });

      this.gameState.giveUp();
      this.render();
    } catch (err) {
      this.viewManager.showGameMessage(`获取答案失败: ${err.message}`, 'error');
    } finally {
      this.setLoading(false);
    }
  }

  setStealthMode(enabled) {
    if (this.config.stealthMode === enabled) return;
    this.config = { ...this.config, stealthMode: enabled };
    this.gameState.setConfig(this.config);
    this.onConfigChange(this.config);
    this.viewManager.clearQuestionInput();
    this.viewManager.clearGuessInput();
    this.viewManager.clearChatInput();
    this.viewManager.setMode(enabled ? 'stealth' : 'normal', this.gameState.get(), this.config);
  }

  setLoading(loading) {
    this.isLoading = loading;
    this.viewManager.setLoading(loading, this.gameState.get(), this.config);
  }

  render() {
    this.viewManager.render(this.gameState.get(), this.config);
  }

  loadUsedNames() {
    try {
      const raw = sessionStorage.getItem('fggg_used_names');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  saveUsedNames() {
    sessionStorage.setItem('fggg_used_names', JSON.stringify(this.usedNames));
  }

  loadUsedCases() {
    try {
      const raw = sessionStorage.getItem('fggg_used_cases');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  saveUsedCases() {
    sessionStorage.setItem('fggg_used_cases', JSON.stringify(this.usedCases));
  }
}
